import type {
  PublicHistoryDay,
  PublicServiceDetail,
  PublicServiceStatus,
} from "@/v2-status-client/status/status.types"

// Demo helpers: pure functions that build the fixture data the
// `?demo=…` URL switches turn on. Lives next to status.demo.ts so
// the fixture file stays purely data; logic that fans the data out
// (history strips, sparklines, service rows) is here.

const DAYS = 30
const HOURS = 24

// Fill a 30-day history strip from the right so the latest day
// lands on the rightmost cell -- matches what the live API does.
export function buildHistory(
  pattern: ReadonlyArray<PublicServiceStatus>,
): PublicHistoryDay[] {
  const today = new Date()
  today.setUTCHours(0, 0, 0, 0)
  return Array.from({ length: DAYS }, (_, i) => {
    const day = new Date(today)
    day.setUTCDate(day.getUTCDate() - (DAYS - 1 - i))
    const status = pattern[i] ?? "operational"
    const uptimePercent =
      status === "operational" ? 100 : status === "degraded" ? 99.4 : 92.5
    return {
      date: day.toISOString().slice(0, 10),
      status,
      uptimePercent,
      incidentMinutes:
        status === "operational" ? 0 : status === "degraded" ? 9 : 110,
    }
  })
}

// Build a 24-bucket sparkline by walking a sine wave around `base`
// ms with a `swing` ms amplitude. Deterministic-by-seed so the same
// service id always renders the same line across reloads (otherwise
// the strips animate every poll, which reads as a bug).
export function buildSparkline(
  seed: number,
  base: number,
  swing: number,
): Array<number | null> {
  return Array.from({ length: HOURS }, (_, i) => {
    const phase = (i + seed) * 0.42
    const v = base + Math.sin(phase) * swing
    return Math.max(8, Math.round(v))
  })
}

// Human-readable name for a demo service. The fixture uses the
// same id-as-name pattern the live page shows after `humaniseTargetName`
// runs in the Go backend ("Apps Service 04", "Maplayer Service 12"),
// so the layout (truncation, line-height, description block) gets
// exercised against text that looks like the production output --
// no `v2-` prefixes, no `@release` suffixes, no dashes-in-the-name
// edge cases sneaking past review.
function demoServiceName(prefix: string, index: number): string {
  const display = `${prefix}-service-${String(index + 1).padStart(2, "0")}`
  return display
    .split("-")
    .map((part) =>
      part.length === 0
        ? part
        : part[0].toUpperCase() + part.slice(1).toLowerCase(),
    )
    .join(" ")
}

// Short fixture description so the demo exercises the
// description block visually. Real per-service copy comes from the
// backend catalog; here we just want enough text to see how the
// layout breathes when a paragraph sits between the title and the
// metadata strip.
function demoServiceDescription(prefix: string, provider?: string): string {
  const what =
    prefix === "apps"
      ? "core Placepoint API used by the web app"
      : prefix === "basemap"
        ? "background map tile serving the basemap layer"
        : prefix === "maplayer"
          ? "individual map layer rendered in the layer picker"
          : prefix === "navlayer"
            ? "top-level layer group in the navigation menu"
            : prefix === "v1-apps"
              ? "legacy Placepoint v1 service"
              : prefix === "propcloud"
                ? "Propcloud customer-facing experience"
                : prefix === "newsecmaps"
                  ? "Newsecmaps API endpoint"
                  : prefix === "newsecmaps-deps"
                    ? "third-party map service Newsecmaps depends on"
                    : prefix === "azure"
                      ? "Microsoft Azure platform service"
                      : prefix === "aws"
                        ? "Amazon Web Services platform service"
                        : prefix === "gcp"
                          ? "Google Cloud Platform service"
                          : "monitored service"
  return provider ? `${what} (provided by ${provider}).` : `${what}.`
}

// Build a small list of demo services with realistic latency/status.
// Spread is what makes the expanded view interesting -- a couple
// of slow services and (optionally) a single down one so reviewers
// can see all three states. Names are picked to read like real
// service identifiers from the operator side.
export function buildServiceList(
  prefix: string,
  count: number,
  opts: { degradedAt?: number[]; downAt?: number[]; provider?: string } = {},
): PublicServiceDetail[] {
  const { degradedAt = [], downAt = [], provider } = opts
  const out: PublicServiceDetail[] = []
  for (let i = 0; i < count; i++) {
    const isDown = downAt.includes(i)
    const isDegraded = !isDown && degradedAt.includes(i)
    const status: PublicServiceStatus = isDown
      ? "down"
      : isDegraded
        ? "degraded"
        : "operational"
    const base = isDown ? 1200 : isDegraded ? 720 : 240 + (i % 3) * 40
    const avg = isDown ? null : base
    const p95 = isDown ? null : Math.round(base * 1.6)
    const id = `${prefix}-service-${String(i + 1).padStart(2, "0")}`
    const name = demoServiceName(prefix, i)
    out.push({
      // Using a stable slug as the id keeps deep links readable: a
      // customer-support URL like `#service-maplayer-service-04`
      // points at the row with that exact slug. When the fixture is
      // replaced by the live API the backend returns its own stable
      // slug per service, so the alignment carries over.
      id,
      name,
      description: demoServiceDescription(prefix, provider),
      host: provider ? `${prefix}.${slug(provider)}.example.com` : undefined,
      providerName: provider,
      status,
      summary:
        status === "down"
          ? "Unavailable"
          : status === "degraded"
            ? `Slower than usual · ${p95} ms`
            : `Operational · ${avg} ms avg`,
      uptimePercent30d:
        status === "down" ? 92.5 : status === "degraded" ? 99.4 : 99.98,
      avgDurationMs: avg,
      p95DurationMs: p95,
      latencySparkline: isDown
        ? Array.from({ length: HOURS }, () => null)
        : buildSparkline(i * 7, base, base * 0.25),
      // Per-service 30-day strip. We sprinkle a recent disruption
      // on the down/degraded rows so reviewers can see the strip
      // rendering all three colours; healthy rows stay green so the
      // bulk of the demo doesn't look like a recurring outage.
      history: buildHistory(
        Array.from({ length: 30 }, (_, day) => {
          if (isDown && day === 29) return "down"
          if (isDown && day === 28) return "degraded"
          if (isDegraded && day === 28) return "degraded"
          return "operational"
        }),
      ),
      // Down services have no usable hourly samples to draw, so the
      // strip falls back to "no data" rather than the dashed
      // baseline -- there's no avg to synthesise from either. Every
      // other demo row has live values, mirroring the live page when
      // probes are flowing normally.
      latencySparklineKind: isDown ? "none" : "live",
    })
  }
  return out
}

function slug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}
